import { useQuery } from '@tanstack/react-query'
import { BottomSheet } from '@/components/BottomSheet'
import { renderCitationText, SHORT_LAW_NAMES } from '@/pages/chat/chat-utils'
import { apiClient } from '@/lib/api'
import type { Citation } from '@/pages/chat/chat-types'

type CitationSheetProps = {
  citation: Citation | null
  onClose: () => void
}

export function CitationSheet({ citation, onClose }: CitationSheetProps) {
  const inlineText = citation?.text || citation?.text_preview || ''
  const lawCode = citation?.law_code || ''
  const article = citation?.article || ''
  const nodeId = citation?.node_id || ''
  // Если у цитаты НЕТ текста (частый случай для цитат, разобранных из текста ответа — есть
  // law_code+article, но нет snippet), подгружаем текст статьи: article→node_id→content.
  const needFetch = Boolean(citation) && !inlineText && !!lawCode && (!!nodeId || !!article)
  const { data: fetchedText, isLoading, isError } = useQuery({
    queryKey: ['citation-text', lawCode, article, nodeId],
    enabled: needFetch,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      let nid = nodeId
      if (!nid && lawCode && article) {
        const r = await apiClient.resolveArticle(lawCode, article)
        nid = r?.item?.node_id || ''
      }
      if (!nid || !lawCode) return ''
      const node = await apiClient.getLawNode(lawCode, nid)
      return (node?.content || '').trim()
    },
  })

  if (!citation) return null

  const text = inlineText || fetchedText || ''

  return (
    <BottomSheet
      open
      onClose={onClose}
      title={citation.law_name || SHORT_LAW_NAMES[citation.law_code || ''] || citation.law_code || 'Источник'}
      description={
        citation.article
          ? `Статья ${citation.article}`
          : `Источник: ${citation.source === 'mcp' ? 'База законов' : (citation.source || 'Поиск')}`
      }
      maxBodyHeight="72vh"
      contentClassName="citation-sheet"
    >
      <div className="pb-1">
        {citation.title && (
          <p
            className="text-telegram-text/70"
            style={{
              fontSize: '11px',
              marginBottom: '8px',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {citation.title}
          </p>
        )}
        {text ? (
          <p className="text-sm leading-relaxed whitespace-pre-wrap">
            {renderCitationText(text)}
          </p>
        ) : isLoading ? (
          <div className="flex items-center gap-2 text-telegram-hint text-sm py-2">
            <span className="inline-block h-4 w-4 rounded-full border-2 border-telegram-button/30 border-t-telegram-button animate-spin" />
            Загружаю текст статьи…
          </div>
        ) : (
          <p className="text-telegram-hint text-sm">
            {isError ? 'Не удалось загрузить текст статьи.' : 'Текст статьи недоступен.'}
          </p>
        )}
      </div>
    </BottomSheet>
  )
}
